import React, { useEffect, useState, useCallback } from "react";
import { Share2, Loader2, CalendarDays, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useI18n } from "@/i18n";
import { LoadError } from "@/components/LoadError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function News() {
  const { t } = useI18n();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api("/news").then(setNews).catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const share = async (n) => {
    const text = `${n.title}\n\n${n.summary}`;
    if (navigator.share) { try { await navigator.share({ title: n.title, text }); return; } catch { /* user dismissed the native share sheet */ } }
    try { await navigator.clipboard.writeText(text); toast.success(t("copied")); } catch (e) { console.error("Clipboard copy failed", e); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (error) return <LoadError onRetry={load} />;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="text-2xl font-black text-ink">{t("latestUpdates")}</h1>

      {news.length === 0 ? (
        <div className="flex flex-col items-center py-24 text-center text-ink-muted" data-testid="news-empty">
          <Newspaper size={44} className="mb-3 text-line" />
          {t("newsEmpty")}
        </div>
      ) : (
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {news.map((n) => (
          <article key={n.id} data-testid={`news-card-${n.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-line bg-white transition-all hover:-translate-y-0.5 hover:shadow-md">
            {n.image && <button onClick={() => setActive(n)}><img src={n.image} alt="" className="h-48 w-full object-cover" /></button>}
            <div className="flex flex-1 flex-col p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand">{n.category}</span>
                <span className="flex items-center gap-1 text-[11px] text-ink-muted"><CalendarDays size={11} /> {fmt(n.published_at)}</span>
              </div>
              <button onClick={() => setActive(n)} className="mt-1.5 text-left">
                <h3 className="line-clamp-2 font-bold text-ink hover:text-brand">{n.title}</h3>
              </button>
              <p className="mt-1.5 line-clamp-3 flex-1 text-sm text-ink-muted">{n.summary}</p>
              <div className="mt-3 flex items-center justify-between">
                <button onClick={() => setActive(n)} className="text-sm font-bold text-brand hover:underline">{t("newsHeader")}</button>
                <button onClick={() => share(n)} data-testid={`share-news-${n.id}`} className="flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-brand">
                  <Share2 size={14} /> {t("share")}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          {active && (
            <div>
              <DialogHeader>
                <span className="text-[11px] font-bold uppercase tracking-widest text-brand">{active.category} · {fmt(active.published_at)}</span>
                <DialogTitle className="text-left text-xl leading-snug">{active.title}</DialogTitle>
                <DialogDescription className="sr-only">{active.title}</DialogDescription>
              </DialogHeader>
              {active.image && <img src={active.image} alt="" className="mt-3 w-full rounded-xl object-cover" />}
              <p className="mt-4 whitespace-pre-line leading-relaxed text-ink-soft">{active.body || active.summary}</p>
              <button onClick={() => share(active)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-brand-dark">
                <Share2 size={15} /> {t("share")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
